import React, { useState, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { format, parseISO, isValid } from "date-fns";
import clsx from "clsx";

// Helper function to safely format dates
const formatDate = (dateValue) => {
  if (!dateValue) return "-";

  try {
    // Handle different date formats
    let date;
    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === "string") {
      date = parseISO(dateValue);
    } else if (typeof dateValue === "number") {
      date = new Date(dateValue);
    } else {
      return "-";
    }

    if (!isValid(date)) return "-";
    return format(date, "dd/MM/yy HH:mm");
  } catch {
    return "-";
  }
};

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { Table, Button, Tooltip, IconButton } from "../../components/ui";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";

// Icons
const EditIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

const DeleteIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const CheckCircleIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path
      fillRule="evenodd"
      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
      clipRule="evenodd"
    />
  </svg>
);

const QrCodeIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h2M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
    />
  </svg>
);

// Status Icons
const SignalConnectedIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M2 20h2V8H2v12zm4 0h2V4H6v16zm4 0h2v-8h-2v8zm4 0h2V8h-2v12zm4 0h2V2h-2v18z" />
  </svg>
);

const SignalDisconnectedIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M2 20h2V8H2v12zm4 0h2V4H6v16zM22 6.83l-1.17-1.17L18 8.5V4h-2v7.33l-1.17-1.16-1.41 1.41L18 16.17l4.58-4.59-1.41-1.42-.17.17V6.83z" />
  </svg>
);

const SignalWeakIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M2 20h2V8H2v12zm4 0h2V4H6v16z" />
    <path opacity="0.3" d="M10 20h2v-8h-2v8zm4 0h2V8h-2v12zm4 0h2V2h-2v18z" />
  </svg>
);

const SpinnerIcon = ({ className }) => (
  <svg className={clsx("animate-spin", className)} fill="none" viewBox="0 0 24 24">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

// Custom Tooltip Component for Status
const StatusTooltip = ({ title, content, children }) => {
  return (
    <Tooltip
      content={
        <div className="text-center max-w-xs">
          <p className="font-semibold">{title}</p>
          {content && <p className="mt-1 text-xs opacity-80">{content}</p>}
        </div>
      }
      position="top"
    >
      {children}
    </Tooltip>
  );
};

const Connections = () => {
  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(
    confirmationModalInitialState
  );

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = () => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
  }, [setSelectedWhatsApp, setWhatsAppModalOpen]);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, [setQrModalOpen, setSelectedWhatsApp]);

  const handleEditWhatsApp = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId: whatsAppId,
      });
    }

    if (action === "delete") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId: whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      } catch (err) {
        toastError(err);
      }
    }

    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }

    setConfirmModalInfo(confirmationModalInitialState);
  };

  const renderActionButtons = (whatsApp) => {
    return (
      <div className="flex items-center justify-center gap-2">
        {whatsApp.status === "qrcode" && (
          <Button size="small" onClick={() => handleOpenQrModal(whatsApp)}>
            {i18n.t("connections.buttons.qrcode")}
          </Button>
        )}
        {whatsApp.status === "DISCONNECTED" && (
          <>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleStartWhatsAppSession(whatsApp.id)}
            >
              {i18n.t("connections.buttons.tryAgain")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => handleRequestNewQrCode(whatsApp.id)}
            >
              {i18n.t("connections.buttons.newQr")}
            </Button>
          </>
        )}
        {(whatsApp.status === "CONNECTED" ||
          whatsApp.status === "PAIRING" ||
          whatsApp.status === "TIMEOUT") && (
          <Button
            size="small"
            variant="outlined"
            color="danger"
            onClick={() => {
              handleOpenConfirmationModal("disconnect", whatsApp.id);
            }}
          >
            {i18n.t("connections.buttons.disconnect")}
          </Button>
        )}
        {whatsApp.status === "OPENING" && (
          <Button size="small" variant="outlined" disabled>
            {i18n.t("connections.buttons.connecting")}
          </Button>
        )}
      </div>
    );
  };

  const renderStatusToolTips = (whatsApp) => {
    return (
      <div className="flex items-center justify-center">
        {whatsApp.status === "DISCONNECTED" && (
          <StatusTooltip
            title={i18n.t("connections.toolTips.disconnected.title")}
            content={i18n.t("connections.toolTips.disconnected.content")}
          >
            <SignalDisconnectedIcon className="w-6 h-6 text-danger-500" />
          </StatusTooltip>
        )}
        {whatsApp.status === "OPENING" && (
          <SpinnerIcon className="w-6 h-6 text-success-500" />
        )}
        {whatsApp.status === "qrcode" && (
          <StatusTooltip
            title={i18n.t("connections.toolTips.qrcode.title")}
            content={i18n.t("connections.toolTips.qrcode.content")}
          >
            <QrCodeIcon className="w-6 h-6 text-dark-500" />
          </StatusTooltip>
        )}
        {whatsApp.status === "CONNECTED" && (
          <StatusTooltip title={i18n.t("connections.toolTips.connected.title")}>
            <SignalConnectedIcon className="w-6 h-6 text-success-500" />
          </StatusTooltip>
        )}
        {(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
          <StatusTooltip
            title={i18n.t("connections.toolTips.timeout.title")}
            content={i18n.t("connections.toolTips.timeout.content")}
          >
            <SignalWeakIcon className="w-6 h-6 text-warning-500" />
          </StatusTooltip>
        )}
      </div>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      <QrcodeModal
        open={qrModalOpen}
        onClose={handleCloseQrModal}
        whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
      />
      <WhatsAppModal
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
      />
      <MainHeader>
        <Title>{i18n.t("connections.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button onClick={handleOpenWhatsAppModal}>
            {i18n.t("connections.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className="flex-1 overflow-auto bg-white dark:bg-dark-800 rounded-xl border border-dark-200 dark:border-dark-700 shadow-sm">
        <Table>
          <Table.Head>
            <Table.Row hover={false}>
              <Table.HeaderCell align="center">
                {i18n.t("connections.table.name")}
              </Table.HeaderCell>
              <Table.HeaderCell align="center">
                {i18n.t("connections.table.status")}
              </Table.HeaderCell>
              <Table.HeaderCell align="center">
                {i18n.t("connections.table.session")}
              </Table.HeaderCell>
              <Table.HeaderCell align="center">
                {i18n.t("connections.table.lastUpdate")}
              </Table.HeaderCell>
              <Table.HeaderCell align="center">
                {i18n.t("connections.table.default")}
              </Table.HeaderCell>
              <Table.HeaderCell align="center">
                {i18n.t("connections.table.actions")}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <Table.Loading columns={6} rows={3} />
            ) : (
              <>
                {whatsApps?.length > 0 ? (
                  whatsApps.map((whatsApp) => (
                    <Table.Row key={whatsApp.id}>
                      <Table.Cell align="center">
                        <span className="font-medium text-dark-900 dark:text-white">
                          {whatsApp.name}
                        </span>
                      </Table.Cell>
                      <Table.Cell align="center">
                        {renderStatusToolTips(whatsApp)}
                      </Table.Cell>
                      <Table.Cell align="center">
                        {renderActionButtons(whatsApp)}
                      </Table.Cell>
                      <Table.Cell align="center">
                        {formatDate(whatsApp.updatedAt)}
                      </Table.Cell>
                      <Table.Cell align="center">
                        {whatsApp.isDefault && (
                          <div className="flex items-center justify-center">
                            <CheckCircleIcon className="w-6 h-6 text-success-500" />
                          </div>
                        )}
                      </Table.Cell>
                      <Table.Cell align="center">
                        <div className="flex items-center justify-center gap-1">
                          <IconButton
                            size="small"
                            onClick={() => handleEditWhatsApp(whatsApp)}
                          >
                            <EditIcon className="w-4 h-4" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="danger"
                            onClick={() => {
                              handleOpenConfirmationModal("delete", whatsApp.id);
                            }}
                          >
                            <DeleteIcon className="w-4 h-4" />
                          </IconButton>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))
                ) : (
                  <Table.Empty message={i18n.t("connections.table.empty") || "Nenhuma conexão encontrada"} />
                )}
              </>
            )}
          </Table.Body>
        </Table>
      </div>
    </MainContainer>
  );
};

export default Connections;
